import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

import { hasDigitsAndLettersOnly, hasNoSpecChars } from '@ideals/utils';

import { IPasswordComplexity } from '../interfaces';

export const CustomValidators = {
  whitespaceValidator: (control: AbstractControl): ValidationErrors => {
    const value = (control.value as string | null) ?? '';
    const onlyWhitespace = value.trim().length === 0;

    return onlyWhitespace ? { required: true } : undefined;
  },

  digitsAndLatinOnly: (control: AbstractControl): ValidationErrors => {
    const value = control.value as string;

    return hasDigitsAndLettersOnly(value) ? undefined : { digitsAndLatinOnly: true };
  },

  specCharValidator: (control: AbstractControl): ValidationErrors => {
    const value = control.value as string;

    return hasNoSpecChars(value) ? undefined : { specChar: true };
  },

  allCharsValid: (): ValidationErrors => {
    return (control: AbstractControl): ValidationErrors | null => {
      const pwd = control.value as string;

      if (!pwd) {
        return undefined;
      }

      const allCharsInvalid = /[^A-Za-z0-9~`!@#$%^&*()_+-={}[\]\\|:";'<>?,./]+/.test(pwd);

      return allCharsInvalid ? { allCharsInvalid } : undefined;
    };
  },

  uppercaseLetter: (uppercaseLetterTemp: number): ValidationErrors => {
    return (control: AbstractControl): ValidationErrors | null => {
      const pwd = control.value as string;

      if (!pwd) {
        return undefined;
      }

      const uppercaseLetter = new RegExp(`^(?=.*[A-Z]{${uppercaseLetterTemp}})`).test(pwd);

      return uppercaseLetter ? undefined : { uppercaseLetter };
    };
  },

  lowercaseLetter: (lowercaseLetterTemp: number): ValidationErrors => {
    return (control: AbstractControl): ValidationErrors | null => {
      const pwd = control.value as string;

      if (!pwd) {
        return undefined;
      }

      const lowercaseLetter = new RegExp(`^(?=.*[a-z]{${lowercaseLetterTemp}})`).test(pwd);

      return lowercaseLetter ? undefined : { lowercaseLetter };
    };
  },

  digitOrSpecChars: (digitOrSpecCharsTemp: number): ValidationErrors => {
    return (control: AbstractControl): ValidationErrors | null => {
      const pwd = control.value as string;

      if (!pwd) {
        return undefined;
      }

      const digitOrSpecChars = new RegExp(
        `^(?=.*[0-9~\`!@#$%^&*()_+-={}[\\]\\\\|:";'<>?,./]{${
          digitOrSpecCharsTemp
        }})`,
      ).test(pwd);

      return digitOrSpecChars ? undefined : { digitOrSpecChars };
    };
  },

  confirmPasswordValidator: (control: AbstractControl): ValidationErrors => {
    if (!control.get) {
      return undefined;
    }

    const pwd = control.get('password').value as string;
    const confirmPwd = control.get('confirmPwd').value as string;

    if (pwd && confirmPwd && pwd !== confirmPwd) {
      return { confirmMatch: true };
    }

    return undefined;
  },

  // Note: based on https://github.com/ideals-vdr/ideals.shared/blob/main/Common/iDeals.Common.Email/Validation/EmailAddressValidator.cs
  // to unify email validation with BE
  emailAddressValidator: (control: AbstractControl): ValidationErrors => {
    const maxLength = 254;
    const atChar = '@';
    const periodChar = '.';
    const emailAddress = (control.value as string | null) ?? '';

    if (emailAddress.length > maxLength) {
      return { email: true };
    }

    const lastCharIndex = emailAddress.length - 1;

    if (emailAddress.startsWith(atChar)
      || emailAddress[lastCharIndex] === atChar
      || emailAddress.startsWith(periodChar)
      || emailAddress[lastCharIndex] === periodChar
    ) {
      return { email: true };
    }

    const atCharIndex = emailAddress.indexOf(atChar);

    if (atCharIndex !== emailAddress.lastIndexOf(atChar)) {
      return { email: true };
    }

    if (atCharIndex < 1
      || emailAddress[atCharIndex - 1] === periodChar
      || emailAddress[atCharIndex + 1] === periodChar
    ) {
      return { email: true };
    }

    if (!emailAddress.includes(periodChar, atCharIndex + 1)) {
      return { email: true };
    }

    return undefined;
  },
};

export function passwordValidator(rules: IPasswordComplexity[]): ValidatorFn {
  return (control: AbstractControl): ValidationErrors => {
    const pwd = control.value as string;
    const errors: ValidationErrors = {};

    rules.forEach((rule) => {
      let isValid = true;

      switch (rule.type) {
        case 'regex': {
          isValid = new RegExp(rule.params[0] as string).test(pwd);
          break;
        }
        case 'minLength': {
          isValid = (rule.params[0] as number) <= pwd.length;
          break;
        }
        case 'maxLength': {
          isValid = (rule.params[0] as number) >= pwd.length;
          break;
        }
        default: {
          return;
        }
      }

      if (!isValid) {
        errors[rule.message] = true;
      }
    });

    return errors;
  };
}
